/**
 * Version checking utility to detect new deployments and force reload
 * This ensures users always have the latest version without manual refresh
 */

let currentVersion: string | null = null
let checkInterval: NodeJS.Timeout | null = null

/**
 * Fetches the current version from the server
 */
async function fetchVersion(): Promise<string | null> {
  try {
    const response = await fetch('/version.json?' + Date.now(), {
      cache: 'no-cache',
      headers: {
        'Cache-Control': 'no-cache',
        'Pragma': 'no-cache'
      }
    })
    
    if (!response.ok) {
      console.warn('Failed to fetch version.json')
      return null
    }
    
    const data = await response.json()
    return data.version || null
  } catch (error) {
    console.warn('Error fetching version:', error)
    return null
  }
}

/**
 * Checks if a new version is available and reloads if necessary
 */
async function checkForNewVersion(): Promise<void> {
  const newVersion = await fetchVersion()
  
  if (!newVersion) {
    return
  }
  
  // Set initial version on first check
  if (currentVersion === null) {
    currentVersion = newVersion
    console.log('App version:', currentVersion)
    return
  }
  
  // Compare versions
  if (newVersion !== currentVersion) {
    console.log(`New version detected: ${newVersion} (current: ${currentVersion})`)
    console.log('Reloading to get latest version...')
    
    // Clear the interval before reload
    if (checkInterval) {
      clearInterval(checkInterval)
    }
    
    // Force reload without cache
    window.location.reload()
  }
}

/**
 * Starts periodic version checking
 * @param intervalMs - How often to check for updates (default: 5 minutes)
 */
export function startVersionCheck(intervalMs: number = 5 * 60 * 1000): void {
  // Initial check
  checkForNewVersion()
  
  // Periodic checks
  checkInterval = setInterval(() => {
    checkForNewVersion()
  }, intervalMs)
  
  // Also check when tab becomes visible (user returns to the app)
  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) {
        checkForNewVersion()
      }
    })
  }
}

/**
 * Stops version checking
 */
export function stopVersionCheck(): void {
  if (checkInterval) {
    clearInterval(checkInterval)
    checkInterval = null
  }
}
